import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

// Polls /api/marketplace/unread-summary and exposes a shared counter to the
// AppHeader (Marketplace nav badge) and any page that needs per-listing counts.
// Auto-refresh: every 20s while the user is authenticated + on-demand via `refresh()`.

const MarketplaceUnreadContext = createContext({ total: 0, perListing: [], refresh: () => {} });

export function useMarketplaceUnread() {
  return useContext(MarketplaceUnreadContext);
}

export function MarketplaceUnreadProvider({ children }) {
  const { user } = useAuth();
  const [total, setTotal] = useState(0);
  const [perListing, setPerListing] = useState([]);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) { setTotal(0); setPerListing([]); return; }
    try {
      const { data } = await api.get("/marketplace/unread-summary");
      setTotal(data.total || 0);
      setPerListing(data.per_listing || []);
    } catch {
      // silent fail — keep previous counts
    }
  }, [user]);

  useEffect(() => {
    refresh();
    if (timerRef.current) clearInterval(timerRef.current);
    if (user) timerRef.current = setInterval(refresh, 20000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [user, refresh]);

  // Also refresh when the tab regains focus.
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  return (
    <MarketplaceUnreadContext.Provider value={{ total, perListing, refresh }}>
      {children}
    </MarketplaceUnreadContext.Provider>
  );
}
